import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button, Modal, Spinner } from "react-bootstrap";
import {
  cancelPayment,
  getPaymentOrderDetails,
} from "../../../lib/store/actions";
import { showToast } from "../../../helper/common";
import styles from "./Payment.module.scss";
import { useLanguage } from "../../../hooks/useLanguage";

function PaymentResultPage() {
  const t = useLanguage();

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [statusMessage, setStatusMessage] = useState(
    "Waiting for payment confirmation...",
  );
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(true);

  const query = new URLSearchParams(location.search);
  const orderId = query.get("order_id");

  useEffect(() => {
    const runEffect = async () => {
      if (!orderId) {
        setStatusMessage("No order ID provided.");
        setLoading(false);
        return;
      }

      let bodyParms = { order_id: orderId };

      const checkPayment = async (attempts = 0) => {
        setLoading(true);
        if (attempts > 12) {
          const pendingPayment = localStorage.getItem("pendingPayment");
          if (pendingPayment) {
            const payment = JSON.parse(pendingPayment);
            dispatch(
              cancelPayment({
                data: { purchaseID: payment?.purchaseID },
                callBack: () => {
                  localStorage.removeItem("pendingPayment");
                },
              }),
            );
          }
          throw new Error("Timeout waiting for payment");
        }

        try {
          const res = await dispatch(
            getPaymentOrderDetails({ data: bodyParms }),
          ).unwrap();
          if (res?.data && res?.data?.status !== "new") {
            localStorage.removeItem("pendingPayment");
            setLoading(false);
            const status = res.data.status;
            let message = "✅ Transaction successful. Redirecting...";
            if (
              ["error", "rejected", "reversed", "fraud", "declined"].includes(
                status,
              )
            ) {
              message = `❌ Transaction ${status}. Redirecting...`;
            } else if (status === "prepared") {
              message = "✅ Transaction prepared. Please approve the request!";
            }
            setStatusMessage(message);
            setTimeout(() => navigate("/game"), 3000);
          } else {
            await new Promise((resolve) => setTimeout(resolve, 10000));
            return await checkPayment(attempts + 1);
          }
        } catch (err) {
          console.error("Error checking payment:", err);
          showToast(t("toast.package_payment_timeout"), "error");
          setLoading(false);
        }
      };
      await checkPayment();
    };

    runEffect();
  }, [orderId, navigate, dispatch]);

  useEffect(() => {
    const pendingPayment = localStorage.getItem("pendingPayment");
    if (pendingPayment) {
      const payment = JSON.parse(pendingPayment);
      const paymentStartTime = new Date(payment.timestamp);
      const now = new Date();
      const timeDiff = now - paymentStartTime;
      const fifteenMinutes = 15 * 60 * 1000;

      if (timeDiff > fifteenMinutes) {
        localStorage.removeItem("pendingPayment");
        dispatch(
          cancelPayment({
            data: { purchaseID: payment?.purchaseID },
            callBack: () => {},
          }),
        );
      }
    }
  }, [dispatch]);

  const submitCancel = () => {
    const pendingPayment = localStorage.getItem("pendingPayment");
    if (pendingPayment) {
      const payment = JSON.parse(pendingPayment);
      setLoading(true);
      dispatch(
        cancelPayment({
          data: { purchaseID: payment?.purchaseID },
          callBack: () => {
            localStorage.removeItem("pendingPayment");
            setLoading(false);
            setStatusMessage(t("toast.transaction_cancelled"));
            setTimeout(() => navigate("/game"), 2000);
          },
        }),
      );
    } else {
      setTimeout(() => navigate("/game"), 2000);
    }
  };

  const handledHide = () => {
    localStorage.removeItem("pendingPayment");
    setTimeout(() => navigate("/game"), 2000);
  };

  return (
    <Modal
      show={showModal}
      backdrop="static"
      className={`${styles.paymentPop} bottomPop`}
      centered
      keyboard={false}
      size="md"
    >
      <Modal.Header className="text-white text-center border-0 pb-0">
        <Modal.Title className="w-100 mb-0">Payment Status</Modal.Title>
      </Modal.Header>

      <Modal.Body className="text-white text-center border-0 py-4">
        <p style={{ fontSize: "1.1rem", marginBottom: "1.5rem" }}>
          {statusMessage}
        </p>
        {loading && <Spinner variant="primary" size="sm" />}
      </Modal.Body>

      <div className="px-3 pb-3 border-0">
        <div className="row g-3">
          <div className="col-6">
            <Button
              onClick={handledHide}
              variant="primary"
              className="w-100 d-flex align-items-center justify-content-center fw-bold commonBtn border-0"
              style={{
                minHeight: "45px",
                borderRadius: "8px",
              }}
            >
              Continue
            </Button>
          </div>
          <div className="col-6">
            <Button
              className={`w-100 d-flex align-items-center justify-content-center fw-bold commonBtn border-0`}
              variant="danger"
              onClick={submitCancel}
              style={{
                minHeight: "45px",
                borderRadius: "8px",
              }}
            >
              Cancel Payment
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default PaymentResultPage;
